import React from "react";
import { Button, Card, Col, Progress, Row, Space, Statistic, Tag, Typography, theme as antdTheme, Table, Divider } from "antd";
import {
  ThunderboltOutlined,
  VideoCameraOutlined,
  FolderOpenOutlined,
  PlayCircleOutlined,
  UserOutlined,
  CrownOutlined,
  DollarOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import { useNavigation } from "@refinedev/core";
import dayjs from "dayjs";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { useDashboardStats } from "./useDashboardStats";
import { getStatusMeta } from "../movies/shared";

const metricCardStyle: React.CSSProperties = {
  height: "100%",
};

// 订阅分布饼图颜色
const PIE_COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042", "#a4de6c"];

export const DashboardOverview: React.FC = () => {
  const { token } = antdTheme.useToken();
  const { stats, loading: statsLoading } = useDashboardStats();
  const { list, show, edit } = useNavigation();
  const topMovies = stats.topMovies ?? [];
  const topMovieLeader = topMovies[0];
  const totalTopViews = topMovies.reduce((sum, movie) => sum + movie.viewCount, 0);
  const averagePopularity =
    topMovies.length > 0
      ? Math.round(
          topMovies.reduce((sum, movie) => sum + movie.popularity, 0) /
            topMovies.length
        )
      : 0;
  const averageScore =
    topMovies.length > 0
      ? Number(
          (
            topMovies.reduce((sum, movie) => sum + movie.voteAverage, 0) /
            topMovies.length
          ).toFixed(1)
        )
      : 0;

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Space align="center">
              <VideoCameraOutlined
                style={{ fontSize: 32, color: token.colorPrimary }}
              />
              <Statistic
                title="影片总量"
                value={stats.totalMovies}
                suffix="部"
                loading={statsLoading}
              />
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Space align="center">
              <PlayCircleOutlined
                style={{ fontSize: 32, color: token.colorSuccess }}
              />
              <Statistic
                title="已发布"
                value={stats.publishedMovies}
                suffix="部"
                loading={statsLoading}
              />
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Space align="center">
              <FolderOpenOutlined
                style={{ fontSize: 32, color: token.colorWarning }}
              />
              <Statistic
                title="分类数量"
                value={stats.totalCategories}
                suffix="个"
                loading={statsLoading}
              />
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Space align="center">
              <ThunderboltOutlined
                style={{ fontSize: 32, color: token.colorError }}
              />
              <Statistic
                title="排期影片"
                value={stats.scheduledMovies}
                suffix="部"
                loading={statsLoading}
              />
            </Space>
          </Card>
        </Col>
      </Row>

      <Divider orientation="left">内容表现</Divider>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card
            title="播放热榜（按观看次数）"
            loading={statsLoading}
            extra={
              <Typography.Text type="secondary">
                Top 8 · 实时同步播放热度
              </Typography.Text>
            }
          >
            <Table
              dataSource={topMovies}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 960 }}
            >
              <Table.Column
                title="#"
                dataIndex="rank"
                key="rank"
                width={60}
                render={(_, __, index) => (
                  <Typography.Text strong>{index + 1}</Typography.Text>
                )}
              />
              <Table.Column
                title="影片"
                dataIndex="title"
                key="title"
                ellipsis
                render={(text: string, record: any) => {
                  const statusMeta = getStatusMeta(record.status);
                  return (
                    <Space size="small">
                      <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
                      <Typography.Text>{text}</Typography.Text>
                    </Space>
                  );
                }}
              />
              <Table.Column
                title="观看次数"
                dataIndex="viewCount"
                key="viewCount"
                width={140}
                render={(value: number) => (
                  <Typography.Text strong>
                    {(value ?? 0).toLocaleString()} 次
                  </Typography.Text>
                )}
              />
              <Table.Column
                title="人气指数"
                dataIndex="popularity"
                key="popularity"
                width={140}
                render={(value: number) => (
                  <Tag color="processing">{Math.round(value ?? 0)}</Tag>
                )}
              />
              <Table.Column
                title="评分"
                dataIndex="voteAverage"
                key="voteAverage"
                width={120}
                render={(value: number) => (
                  <Typography.Text>{(value ?? 0).toFixed(1)}</Typography.Text>
                )}
              />
              <Table.Column
                title="上线"
                dataIndex="releaseDate"
                key="releaseDate"
                width={140}
                render={(value: string | null) =>
                  value ? dayjs(value).format("YYYY-MM-DD") : "未排期"
                }
              />
              <Table.Column
                title="操作"
                key="actions"
                width={160}
                render={(_, record: any) => (
                  <Space size="small">
                    <Button
                      type="link"
                      size="small"
                      onClick={() => show("movies", record.id)}
                    >
                      查看
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => edit("movies", record.id)}
                    >
                      编辑
                    </Button>
                  </Space>
                )}
              />
            </Table>
            <Typography.Text type="secondary">
              数据来源：影片 view_count / popularity，帮助识别需要追加带宽或营销资源的内容。
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="运营洞察" loading={statsLoading}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Statistic
                title="热门榜总观看"
                value={totalTopViews}
                suffix="次"
              />
              <Statistic
                title="榜单平均人气"
                value={averagePopularity}
                suffix="分"
              />
              <Statistic
                title="榜单平均评分"
                value={averageScore}
                precision={1}
                suffix="/10"
              />
              <Space align="start">
                <Tag color="blue">热度冠军</Tag>
                <Space direction="vertical" size={4}>
                  <Typography.Text strong>
                    {topMovieLeader?.title ?? "暂无热门影片"}
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    {topMovieLeader
                      ? `${topMovieLeader.viewCount.toLocaleString()} 次观看 · 评分 ${(topMovieLeader.voteAverage ?? 0).toFixed(1)}`
                      : "发布/上线影片后自动更新榜单"}
                  </Typography.Text>
                </Space>
              </Space>
              <Button type="primary" block onClick={() => list("movies")}>
                去影片列表运营
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 会员统计区域 */}
      <Divider orientation="left">会员统计</Divider>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Space align="center">
              <UserOutlined
                style={{ fontSize: 32, color: token.colorPrimary }}
              />
              <Statistic
                title="总用户数"
                value={stats.membership.totalUsers}
                suffix="人"
                loading={statsLoading}
              />
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Space align="center">
              <CrownOutlined
                style={{ fontSize: 32, color: "#faad14" }}
              />
              <Statistic
                title="付费用户"
                value={stats.membership.paidUsers}
                suffix="人"
                loading={statsLoading}
                valueStyle={{ color: "#3f8600" }}
              />
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Space align="center">
              <DollarOutlined
                style={{ fontSize: 32, color: "#52c41a" }}
              />
              <Statistic
                title="总收入"
                value={(stats.membership.totalRevenueCents / 100).toFixed(2)}
                prefix="¥"
                loading={statsLoading}
                valueStyle={{ color: "#3f8600" }}
              />
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card style={metricCardStyle}>
            <Space align="center">
              <GiftOutlined
                style={{ fontSize: 32, color: token.colorError }}
              />
              <Statistic
                title="兑换码数量"
                value={stats.membership.activeCodes}
                suffix="个"
                loading={statsLoading}
              />
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 用户增长趋势图 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="用户增长趋势（最近30天）" loading={statsLoading}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats.userGrowth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => dayjs(value).format("MM-DD")}
                />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip
                  labelFormatter={(value) => dayjs(value).format("YYYY-MM-DD")}
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="newUsersCount"
                  stroke="#8884d8"
                  name="新增用户"
                  strokeWidth={2}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="cumulativeTotal"
                  stroke="#82ca9d"
                  name="累计用户"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="订阅分布" loading={statsLoading}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.subscriptionDistribution}
                  dataKey="userCount"
                  nameKey="tierDisplayName"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={(entry: any) => `${entry.tierDisplayName}: ${entry.userCount}人`}
                >
                  {stats.subscriptionDistribution.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* 热门兑换码排行 */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card title="热门兑换码排行（按批次）" loading={statsLoading}>
            <Table
              dataSource={stats.popularCodes}
              rowKey="batchId"
              pagination={false}
              scroll={{ x: 800 }}
            >
              <Table.Column
                title="批次ID"
                dataIndex="batchId"
                key="batchId"
                width={200}
              />
              <Table.Column
                title="会员等级"
                dataIndex="tierDisplayName"
                key="tierDisplayName"
                render={(text, record: any) => (
                  <Tag color="blue">{text}</Tag>
                )}
              />
              <Table.Column
                title="总数量"
                dataIndex="totalCodes"
                key="totalCodes"
              />
              <Table.Column
                title="已使用"
                dataIndex="usedCodes"
                key="usedCodes"
              />
              <Table.Column
                title="使用率"
                dataIndex="usageRate"
                key="usageRate"
                render={(rate: number) => (
                  <Progress
                    percent={rate}
                    size="small"
                    format={(percent) => `${percent}%`}
                  />
                )}
              />
              <Table.Column
                title="收入"
                dataIndex="totalRevenueCents"
                key="totalRevenueCents"
                render={(cents: number) => (
                  <Typography.Text strong style={{ color: "#3f8600" }}>
                    ¥{(cents / 100).toFixed(2)}
                  </Typography.Text>
                )}
              />
            </Table>
          </Card>
        </Col>
      </Row>

      {/* 运营管理与最近上架模块移除，专注关键指标 */}
    </Space>
  );
};
